import { Injectable } from '@angular/core';
import { StorageType } from '../storage-service/storage.service';

const ACCESS_TOKEN = 'access_token';
const REFRESH_TOKEN = 'refresh_token';

@Injectable({ providedIn: 'root' })
export class TokenService {
    private storage: Storage = localStorage;

    /**
     * Backup of the session token, because mobile Safari can drop sessionStorage while a tab is in the
     * background. Not used for localStorage tokens, so signing out in another tab still works.
     */
    private sessionTokenInMemory: string | null = null;

    /**
     * The store a token is not currently being written to. Reads try sessionStorage before
     * localStorage, so a stale entry in the other store would shadow or outlive the live one.
     */
    private get otherStore(): Storage {
        return this.storage === sessionStorage ? localStorage : sessionStorage;
    }

    /**
     *  Retrieves the access token from storage. It first checks sessionStorage, and if the token is not found, it checks localStorage.
     *  In session mode, a token lost from storage falls back to the copy held in memory.
     * @returns The access token if found, or null if no token exists.
     */
    getToken(): string | null {
        let result = sessionStorage.getItem(ACCESS_TOKEN);
        if (result === null) {
            result = localStorage.getItem(ACCESS_TOKEN);
        }
        if (result === null && this.storage === sessionStorage) {
            result = this.sessionTokenInMemory;
        }
        return result;
    }

    /**
     *  Retrieves the refresh token from storage. It first checks sessionStorage, and if the token is not found, it checks localStorage.
     * @returns The refresh token if found in either sessionStorage or localStorage, or null if the token does not exist in both storages.
     */
    getRefreshToken(): string | null {
        let result = sessionStorage.getItem(REFRESH_TOKEN);
        if (result === null) {
            result = localStorage.getItem(REFRESH_TOKEN);
        }
        return result;
    }

    /**
     *  Saves the access token to storage.
     * @param token The access token to be saved.
     */
    saveToken(token: string): void {
        this.storage.setItem(ACCESS_TOKEN, token);
        this.otherStore.removeItem(ACCESS_TOKEN);
        this.sessionTokenInMemory = this.storage === sessionStorage ? token : null;
    }

    /**
     * Saves the refresh token to storage.
     * @param refreshToken The refresh token to be saved.
     */
    saveRefreshToken(refreshToken: string): void {
        this.storage.setItem(REFRESH_TOKEN, refreshToken);
        this.otherStore.removeItem(REFRESH_TOKEN);
    }

    /**
     * Removes the access token from both stores and from memory.
     */
    removeToken(): void {
        sessionStorage.removeItem(ACCESS_TOKEN);
        localStorage.removeItem(ACCESS_TOKEN);
        this.sessionTokenInMemory = null;
    }

    /**
     * Removes the refresh token from both stores.
     */
    removeRefreshToken(): void {
        sessionStorage.removeItem(REFRESH_TOKEN);
        localStorage.removeItem(REFRESH_TOKEN);
    }

    /**
     * Sets the storage type for token storage. If the type is SESSION, it uses sessionStorage; if the type is LOCAL, it uses localStorage.
     * @param type The storage type to be set for token storage.
     */
    setStorageType(type: StorageType): void {
        this.storage = type === StorageType.SESSION ? sessionStorage : localStorage;
    }
}
